import { useState, type FormEvent } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const response = await fetch('http://rtxn.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Not JSON, continue
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = (parsed && (parsed as Record<string, unknown>).meta)
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
          : '';
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <section id="contact" className="py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-xs text-foreground-500 font-medium tracking-widest uppercase mb-3">
            Business Cooperation
          </p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-5">
            商务<span className="text-primary-500">合作</span>
          </h2>
          <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
            如果您有广告投放、内容合作、品牌推广或其他商务需求，欢迎通过下方表单联系我们。我们的商务团队将在24小时内与您取得联系。
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-background-50 rounded-2xl p-6 md:p-8">
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    公司/品牌名称
                  </label>
                  <input
                    id="contact-company"
                    name="company"
                    type="text"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    placeholder="请输入您的公司或品牌名称"
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    联系电话
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                    placeholder="请输入您的联系电话"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-type" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  合作类型 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="contact-type"
                  name="cooperation_type"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors cursor-pointer appearance-none"
                  defaultValue=""
                >
                  <option value="" disabled>请选择合作类型</option>
                  <option value="广告投放">广告投放</option>
                  <option value="内容合作">内容合作</option>
                  <option value="品牌推广">品牌推广</option>
                  <option value="媒体合作">媒体合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  合作需求描述 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                  placeholder="请简要描述您的合作需求（500字以内）"
                ></textarea>
                <p className="text-[10px] text-foreground-400 mt-1">最多500字</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-lg bg-secondary-100 text-secondary-700 text-sm flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  提交成功！我们的商务团队将在24小时内与您联系。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-700 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {formError || '提交失败，请稍后重试'}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>提交中...
                  </span>
                ) : (
                  '提交合作需求'
                )}
              </button>
            </form>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-9 h-9 mx-auto mb-2 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-mail-line text-foreground-600"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-0.5">合作邮箱</p>
              <p className="text-sm font-medium text-foreground-950">hello@rtxn.cn</p>
            </div>
            <div className="text-center">
              <div className="w-9 h-9 mx-auto mb-2 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-phone-line text-foreground-600"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-0.5">合作热线</p>
              <p className="text-sm font-medium text-foreground-950">400-888-8888</p>
            </div>
            <div className="text-center">
              <div className="w-9 h-9 mx-auto mb-2 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-map-pin-line text-foreground-600"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-0.5">公司地址</p>
              <p className="text-sm font-medium text-foreground-950">北京市朝阳区望京SOHO</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}